function parseTimeToMinutes(t) {
  const m = String(t || "").match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return h * 60 + mn;
}

function formatMinutesToTime(mins) {
  let h = Math.floor(mins / 60);
  const mn = mins % 60;
  const ap = h >= 12 ? "PM" : "AM";
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${String(mn).padStart(2, "0")} ${ap}`;
}

// Blocking-out only makes sense within a day's actual interview window(s), so
// the picker's time grid is derived from that day's enabled "interviews"
// events instead of a fixed list — it always matches what the scheduler can
// actually assign.
function buildDayTimeSlots(day, slotMinutes) {
  const interviewEvents = (day.events || []).filter(e => e.enabled && e.type === "interviews");
  if (interviewEvents.length === 0) return [];
  const starts = interviewEvents.map(e => parseTimeToMinutes(e.startTime)).filter(v => v != null);
  const ends = interviewEvents.map(e => parseTimeToMinutes(e.endTime)).filter(v => v != null);
  if (!starts.length || !ends.length) return [];
  const start = Math.min(...starts);
  const end = Math.max(...ends);
  const slots = [];
  for (let t = start; t + slotMinutes <= end; t += slotMinutes) slots.push(formatMinutesToTime(t));
  return slots;
}

export default function BlackoutPicker({ value, onChange, settings }) {
  const MAROON = "#6f0000";
  const bw = settings?.boardWeek || {};
  const slotMinutes = bw.slotMinutes || 30;
  const days = (bw.days || []).map((d, i) => ({
    key: String(i + 1),
    label: d.label || `Day ${i + 1}`,
    timeSlots: buildDayTimeSlots(d, slotMinutes),
  }));

  // Firestore forbids nested arrays, so each blackout slot is stored as { day, time }.
  // Read accessors tolerate the legacy [day, time] array shape for any pre-existing data.
  const slotDay = b => (Array.isArray(b) ? b[0] : b.day);
  const slotTime = b => (Array.isArray(b) ? b[1] : b.time);

  function isBlocked(dayKey, time) {
    return (value || []).some(b => slotDay(b) === dayKey && slotTime(b) === time);
  }

  function toggleSlot(dayKey, time) {
    const current = value || [];
    const exists = current.some(b => slotDay(b) === dayKey && slotTime(b) === time);
    if (exists) {
      onChange(current.filter(b => !(slotDay(b) === dayKey && slotTime(b) === time)));
    } else {
      onChange([...current, { day: dayKey, time }]);
    }
  }

  function blockEntireDay(dayKey, timeSlots) {
    const current = (value || []).filter(b => slotDay(b) !== dayKey);
    const allSlots = timeSlots.map(t => ({ day: dayKey, time: t }));
    onChange([...current, ...allSlots]);
  }

  function clearDay(dayKey) {
    onChange((value || []).filter(b => slotDay(b) !== dayKey));
  }

  return (
    <div style={{ border: "1.5px solid #ebebeb", borderRadius: 12, overflow: "hidden" }}>
      {days.map(day => {
        const blockedCount = (value||[]).filter(b => slotDay(b) === day.key).length;
        const allBlocked = day.timeSlots.length > 0 && blockedCount === day.timeSlots.length;
        return (
          <div key={day.key} style={{ borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", background: blockedCount > 0 ? "#fff8f8" : "#fafafa" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#333" }}>
                {day.label}
                {blockedCount > 0 && <span style={{ marginLeft: 8, fontSize: 11, color: MAROON, fontWeight: 600 }}>{blockedCount} slot{blockedCount !== 1 ? "s" : ""} blocked</span>}
              </div>
              {day.timeSlots.length > 0 && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" onClick={() => allBlocked ? clearDay(day.key) : blockEntireDay(day.key, day.timeSlots)}
                    style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6, border: `1px solid ${allBlocked ? "#fca5a5" : "#e8e8e8"}`, background: allBlocked ? "#fff1f0" : "white", color: allBlocked ? "#dc2626" : "#555", cursor: "pointer" }}>
                    {allBlocked ? "Clear Day" : "Block Entire Day"}
                  </button>
                </div>
              )}
            </div>
            {day.timeSlots.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 16px", background: "white" }}>
                {day.timeSlots.map(time => {
                  const blocked = isBlocked(day.key, time);
                  return (
                    <button type="button" key={time} onClick={() => toggleSlot(day.key, time)}
                      style={{ padding: "5px 10px", borderRadius: 6, border: `1.5px solid ${blocked ? MAROON : "#e8e8e8"}`, background: blocked ? "#fff0f0" : "white", color: blocked ? MAROON : "#555", fontSize: 11, fontWeight: blocked ? 700 : 500, cursor: "pointer", transition: "all 0.15s" }}>
                      {time}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: "10px 16px", background: "white", fontSize: 12, color: "#bbb" }}>No interviews scheduled this day</div>
            )}
          </div>
        );
      })}
      {(value||[]).length > 0 && (
        <div style={{ padding: "10px 16px", background: "#fff8f8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: MAROON, fontWeight: 600 }}>{(value||[]).length} total slots blocked</span>
          <button type="button" onClick={() => onChange([])} style={{ fontSize: 11, color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Clear All</button>
        </div>
      )}
    </div>
  );
}
